import {
  MarketAccessory,
  MarketButton,
  MarketHeader,
  MarketList,
} from '@market/react'
import qs from 'qs'
import { ReactComponent as ExclamationCircle } from '@market/icons/assets/svg/exclamation-circle.svg'
import { useEffect, useState } from 'react'
import { order as squareOrderApi } from '@squareup/sq-addons-web-sdk'
import { useHistory } from 'react-router-dom'
import { loyaltyApi } from 'api'
import LoadingScreen from 'components/Loading'
import ConnectionError from 'components/ConnectionError'
import { useCustomer, useGetCustomer } from 'hooks/customer'
import { RequestContext } from 'utils/pos'
import { buildTranslate } from 'locales'
import { getOfferName, getOrderDiscount } from 'utils/loyalty'
import { ErrorCode, getErrorCode } from 'utils/api'
import OfferCard from 'components/OfferCard'

const t = buildTranslate('removeInvalidOffer')

type InvalidOffer = {
  name: string
  id: string
  discountId: string
  errorMessage: string
}

const RemoveInvalidOfferScreen = () => {
  const { getCustomer } = useGetCustomer()
  const customer = useCustomer()
  const history = useHistory()
  const [removeOffer, { isLoading: isRemovingOffer }] =
    loyaltyApi().useRemoveOfferMutation()
  const queryParams = qs.parse(window.location.search, {
    ignoreQueryPrefix: true,
  })
  const isCheckoutFlow = queryParams['checking_out'] === 'true'
  const [offer, setOffer] = useState<InvalidOffer | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    async function init() {
      const [customerResult, discount] = await Promise.all([
        getCustomer(RequestContext.SDK),
        getOrderDiscount(),
      ])

      // if there's no customer in the order, go to the main screen
      if (customerResult.isErr()) {
        history.push('/' + history.location.search)
        return
      }

      if (!discount?.name || !discount?.uid) {
        history.push('/customer' + history.location.search)
        return
      }

      setOffer({
        name: getOfferName(discount),
        discountId: discount.uid,
        id: discount.metadata?.redemptionId ?? '',
        errorMessage: discount.metadata?.errorMessage ?? '',
      })
    }

    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function onClickContinue() {
    if (!customer || !offer) {
      return
    }

    setError(false)
    const result = await removeOffer({
      userIdentifier: customer.referenceId,
      redemptionIdentifier: offer.id,
    })

    if (
      result.error &&
      // we can don't want to block the application if it's unable to find the offer on the backend
      // we can continue and remove it from the order
      ![ErrorCode.NO_PRIOR_REDEMPTION].some(
        err => err === getErrorCode(result.error)
      )
    ) {
      setError(true)
      return
    }

    await squareOrderApi.removeDiscount(offer.discountId)

    history.push('/customer' + history.location.search)
  }

  if (!offer) {
    return <LoadingScreen />
  }

  if (error) {
    return <ConnectionError onClickTryAgain={onClickContinue} />
  }

  return (
    <div className="market-grid-container">
      <MarketHeader className="market-grid-item-full">
        <h2>{t('title')}</h2>
        <div style={{ marginBottom: '40px' }}>
          <p>{t('subTitle')}</p>
        </div>
      </MarketHeader>
      <MarketList
        className="market-grid-item-full"
        data-testid="invalid-discounts-list"
      >
        <OfferCard kind="unavailable-offer" offerId={offer.id}>
          <MarketAccessory
            slot="leading-accessory"
            size="image"
            className="addons-warning-icon-container"
          >
            <ExclamationCircle
              width="24"
              height="24"
              className="addons-warning-icon"
            />
          </MarketAccessory>
          <OfferCard.Label>{offer.name}</OfferCard.Label>
          <OfferCard.ErrorText>{offer.errorMessage}</OfferCard.ErrorText>
        </OfferCard>
      </MarketList>
      <MarketButton
        data-testid="finish-btn"
        className="market-grid-item-full"
        isLoading={isRemovingOffer || undefined}
        onClick={onClickContinue}
        rank="primary"
      >
        {isCheckoutFlow ? t('returnToCurrentSale') : t('continue')}
      </MarketButton>
    </div>
  )
}

export default RemoveInvalidOfferScreen
